import { convertToCoverage, getCoverageType } from '@/models/search-filter';

import {
  getSearchPropositions,
  mergeData,
  getCoverageData,
  addCoverage,
  getCoverageGeolocations,
  removeCoverage
} from './services';

// A saved coverage with its geolocation ids swapped for the areas picked in
// the search it was saved from (searchItem.filter.geolocations, with names).
function withGeolocationsOf(coverage, searchItem) {
  const type = getCoverageType(coverage);
  const body = coverage.body[type];
  const picked = (searchItem.filter && searchItem.filter.geolocations) || [];
  if (!body || !Array.isArray(body.geolocations)) return coverage;

  return {
    ...coverage,
    body: {
      ...coverage.body,
      [type]: {
        ...body,
        geolocations: body.geolocations
          .map(id => picked.find(geolocation => geolocation.id === id))
          .filter(Boolean)
      }
    }
  };
}

const actions = {
  async addCoverage({ commit }, searchItem) {
    let result;
    try {
      result = await addCoverage(convertToCoverage(searchItem));
    } catch (error) {
      console.error(error);

      if (error.response) return error.response.status;
      else return -1;
    }

    // The search is saved now, so the save counts whatever follows: Spara
    // again would save it twice (the backend has no duplicate check). The
    // area names are only for the list; when the geolocation service cannot
    // give them, the list takes them from the search on the page.
    let saved;
    try {
      [saved] = await getCoverageGeolocations([result.data]);
    } catch (error) {
      console.error(error);
      saved = withGeolocationsOf(result.data, searchItem);
    }

    commit('addCoverageItem', saved);

    return result.status;
  },

  async deleteCoverage({ commit }, coverageId) {
    await removeCoverage(coverageId);
    commit('removeCoverageItem', coverageId);

    return true;
  },

  async fetchCoverages({ commit }) {
    commit('setLoadingCoverage', true);

    const result = await getCoverageData();

    if (result) {
      result.data.results = await getCoverageGeolocations(result.data.results);
    }

    commit('setCoverageData', result.data);
    commit('setLoadingCoverage', false);
  },

  async fetchSearch(
    { commit, state },
    { rootFilter, merge, skipBoundingBoxAlign }
  ) {
    // The page comes from rootFilter only. o-search seeds it from ?page= once
    // on mount; re-reading the live route here would let the address bar and
    // the state drift apart.
    const searchObject = convertToCoverage(rootFilter);

    commit('setLoading', true);

    try {
      const response = await getSearchPropositions(searchObject);

      if (response) {
        if (state.data && searchObject.fromMapInteraction && !merge) {
          commit('setPoints', response.data.points);
          dispatchMapEvents(
            searchObject,
            response.data,
            searchObject.fromMapInteraction && state.data
          );
        } else if (merge) {
          const mergedData = mergeData(state.data, response.data);
          commit('setData', mergedData);
        } else {
          commit('setData', response.data);
          dispatchMapEvents(
            searchObject,
            response.data,
            skipBoundingBoxAlign ? true : false
          );
        }
      }
    } catch (error) {
      console.error(error);

      if (merge) {
        // Leave the list the user is reading intact and let the caller stop
        // auto-loading; clearing it here would unmount the results wrapper,
        // and not clearing it would leave the scroll sentinel in view to
        // retry the same page forever.
        commit('setLoading', false);
        throw error;
      }

      commit('setData', {});
    }

    commit('setLoading', false);
  },

  async resetFilter({ commit }, type) {
    commit('resetFilter', type);
  }
};

function dispatchMapEvents(searchObject, resultData, skipBoundingBoxAlign) {
  const markerPointsEvent = new CustomEvent('markerPoints', {
    detail: resultData.points
  });

  document.dispatchEvent(markerPointsEvent);

  if (skipBoundingBoxAlign) {
    return;
  }

  if (searchObject.map) {
    document.dispatchEvent(
      new CustomEvent('boundingBox', {
        detail: searchObject.map.bounds
      })
    );
  } else if (resultData.bbox) {
    document.dispatchEvent(
      new CustomEvent('boundingBox', {
        detail: resultData.bbox
      })
    );
  } else if (searchObject.searchArea && searchObject.searchArea.bounds) {
    document.dispatchEvent(
      new CustomEvent('boundingBox', {
        detail: searchObject.searchArea.bounds
      })
    );
  }
}

export default actions;
